/**
 * Datora Discount Entry — state helpers for the discount code field in the cart.
 * The active code is displayed inside the field, the remove (X) button sits in it.
 * Loaded before datora.js so the functions are available globally.
 *
 * Visibility is toggled through the theme's own `.hidden` class (display: none !important),
 * not the hidden attribute: base.css gives buttons and spans author display rules that would
 * override the attribute.
 */

const DATORA_HIDDEN_CLASS = 'hidden';

function datoraContainers() {
  return document.querySelectorAll('.datora-discount');
}

/**
 * What the shopper has typed but not submitted. Every cart update replaces the cart markup,
 * input element included, so the text cannot survive on the element itself. It is kept here
 * and written into the new field by restoreDiscountState().
 */
let datoraDraft = null;

document.addEventListener('input', (event) => {
  const input = event.target;
  if (!input.matches || !input.matches('.datora-discount input[name="datora-discount"]')) return;
  datoraDraft = input.value.trim() ? input.value : null;
});

/** Called on apply and remove: submitting ends the draft. */
function clearDatoraDraft() {
  datoraDraft = null;
}

function applyDatoraDraft() {
  if (!datoraDraft) return;
  datoraContainers().forEach((container) => {
    const input = container.querySelector('input[name="datora-discount"]');
    if (input) input.value = datoraDraft;
  });
}

/**
 * Show the active discount code in the field.
 */
function showActiveDiscountCode(code) {
  const cleanCode = code.trim().toUpperCase();

  datoraContainers().forEach((container) => {
    container.setAttribute('data-discount-state', 'active');

    const removeBtn = container.querySelector('.datora-discount__remove');
    if (removeBtn) removeBtn.classList.remove(DATORA_HIDDEN_CLASS);

    const input = container.querySelector('input[name="datora-discount"]');
    if (input) input.value = cleanCode;
  });
}

/**
 * Clear the active discount code display, return to empty state.
 */
function clearActiveDiscountCode() {
  datoraContainers().forEach((container) => {
    container.setAttribute('data-discount-state', 'empty');

    const removeBtn = container.querySelector('.datora-discount__remove');
    if (removeBtn) removeBtn.classList.add(DATORA_HIDDEN_CLASS);

    const input = container.querySelector('input[name="datora-discount"]');
    if (input) input.value = '';
  });
}

/**
 * Restore the field state after a cart DOM re-render.
 * Called via pub/sub cartUpdate and on co2.cart.initialized.
 * The state (active code, remove button) follows the cart; the text in the field follows
 * the shopper's draft while there is one.
 */
let datoraRestoreSeq = 0;

/**
 * The field is only useful with the app's `co2` library on the page. Without it (embed not
 * enabled yet, blocked script) every apply would fail, so the whole field stays out of sight;
 * it comes back as soon as co2 announces itself (co2.cart.initialized) or a re-render finds it.
 * Runs after `load`, when every deferred and async script, the app embed included, has run.
 */
function syncDatoraFieldAvailability() {
  const available = typeof co2 !== 'undefined';
  datoraContainers().forEach((container) => {
    container.classList.toggle(DATORA_HIDDEN_CLASS, !available);
  });
  return available;
}

async function restoreDiscountState() {
  if (!syncDatoraFieldAvailability()) return;

  // Only the newest call may write: a slow answer from before a remove would otherwise put
  // the removed code back into the field.
  const seq = ++datoraRestoreSeq;
  let code = null;
  try {
    code = await co2.getDiscountCode();
  } catch (error) {
    console.error('[datora] reading the active code failed:', error);
    return;
  }
  if (seq !== datoraRestoreSeq) return;

  if (code) {
    showActiveDiscountCode(code);
  } else {
    clearActiveDiscountCode();
  }
  applyDatoraDraft();
}
